import { HexLocator, ThemeType } from "@hex/common";
import { useMemo } from "react";
import { useLocation } from "react-router";

import { ORG_ID } from "../orgs";
import { isScreenshotPageLocation } from "../util/screenshotPage.js";

import {
  ClientSideRoute,
  ClientSideRouteForCurrentHex,
  Routes,
  ViewType,
  useQueryParams,
} from "./routes";
import { useTargetHexLocator } from "./user/project/HumanHexUrl";

function areWeIFrame(): boolean {
  try {
    // If we aren't the top-most window in the stack, then we're embedded
    return window.self !== window.top;
  } catch {
    // Window-related features might be blocked for us due to same-origin policy.
    // Most likely this is because we're embedded in an iFrame.
    return true;
  }
}

// `isEmbeddedUrl` can be used if `queryParams` is already available.
// `useIsEmbeddedUrl` is a convenience hook which also gets the query params for you.
export function isEmbeddedUrl(queryParams: URLSearchParams): boolean {
  // If embedded doesn't exist, then we attempt to determine if we are actually embedded
  // by using window attributes. This helps in situations where someone is naively embedding
  // an app without using the code in the Share panel.
  const embedded = queryParams.get("embedded");
  if (embedded != null) {
    return embedded === "true";
  }
  return areWeIFrame();
}
export function useIsEmbeddedUrl(): boolean {
  const queryParams = useQueryParams();
  return isEmbeddedUrl(queryParams);
}
export function isSignedEmbeddingApp(queryParams: URLSearchParams): boolean {
  return isEmbeddedUrl(queryParams) && queryParams.get("embeddedToken") != null;
}
export function useIsSignedEmbeddingApp(): boolean {
  const queryParams = useQueryParams();
  return isSignedEmbeddingApp(queryParams);
}

export function useIsNotionEmbeddedUrl(): boolean {
  const queryParams = useQueryParams();
  return queryParams.get("embed_notion") === "true";
}

export function useIsNoEmbedFooterUrl(): boolean {
  const queryParams = useQueryParams();
  return queryParams.get("noEmbedFooter") === "true";
}

export function useIsNoEmbedOutlineUrl(): boolean {
  const queryParams = useQueryParams();
  return queryParams.get("noEmbedOutline") === "true";
}

export function useIsNoEmbedBasePaddingUrl(): boolean {
  const queryParams = useQueryParams();
  return queryParams.get("noEmbedBasePadding") === "true";
}

export function useIsNoEmbedHeaderPaddingUrl(): boolean {
  const queryParams = useQueryParams();
  return queryParams.get("noEmbedHeaderPadding") === "true";
}

export function useShowEmbeddedRunButton(): boolean {
  const queryParams = useQueryParams();
  return queryParams.get("showEmbeddedRunButton") === "true";
}

export function useIsScreenshotUrl(): boolean {
  const { pathname, search } = useLocation();
  return (
    isScreenshotPageLocation({ pathname, search }) ||
    (pathname.includes("/code/") && pathname.endsWith("/screenshot"))
  );
}

/**
 * Returns true when the app is being rendered in thumbnail mode for project cards.
 * In this mode, we hide metadata (title, description, attribution) to capture
 * only the app content for thumbnails.
 */
export function useIsThumbnailModeUrl(): boolean {
  const queryParams = useQueryParams();
  return queryParams.get("thumbnailMode") === "true";
}

export function useThemeOverride(): ThemeType | null {
  const queryParams = useQueryParams();
  const themeParam = queryParams.get("theme");
  return themeParam != null ? (themeParam.toUpperCase() as ThemeType) : null;
}

// We have to cast things to Params because of type bugs in https://github.com/microsoft/TypeScript/issues/28884
const createRouteForCurrentHex = <
  Path extends string,
  Params extends { hexLocator: HexLocator },
>(
  route: ClientSideRoute<Path, Params>,
  hexLocator: HexLocator,
): ClientSideRouteForCurrentHex<Params> => {
  return {
    ...route,
    getUrl: (routeArgs) => {
      return route.getUrl({
        ...routeArgs,
        hexLocator,
      } as Params);
    },
    href: (withOrigin, routeArgs) => {
      return route.href(ORG_ID, withOrigin, {
        ...routeArgs,
        hexLocator,
      } as Params);
    },
    push: (history, routeArgs) => {
      route.push(history, { ...routeArgs, hexLocator } as Params);
    },
    replace: (history, routeArgs) => {
      route.replace(history, { ...routeArgs, hexLocator } as Params);
    },
  };
};

export function useRoutesForCurrentHex() {
  const hexLocator = useTargetHexLocator();

  return useMemo(
    () => ({
      APP: createRouteForCurrentHex(Routes.APP, hexLocator),
      APP_BASE: createRouteForCurrentHex(Routes.APP_BASE, hexLocator),
      APP_LATEST: createRouteForCurrentHex(Routes.APP_LATEST, hexLocator),
      LOGIC: createRouteForCurrentHex(Routes.LOGIC, hexLocator),
      COMPONENT: createRouteForCurrentHex(Routes.COMPONENT, hexLocator),
      DEBUG: createRouteForCurrentHex(Routes.DEBUG, hexLocator),
      SPLIT_SCREEN: createRouteForCurrentHex(Routes.SPLIT_SCREEN, hexLocator),
      UI: createRouteForCurrentHex(Routes.UI, hexLocator),
      getTabRoute: (view: ViewType) =>
        createRouteForCurrentHex(Routes.getTabRoute(view), hexLocator),
    }),
    [hexLocator],
  );
}
